import React, { useMemo, useState } from 'react';

import { useTranslation } from '@/i18n';
import PageLayout from '@/components/layout/PageLayout';
import { TabList } from '@/components/ui/navigation/TabList';
import { useHolidayList } from '@/hooks/attendance/holiday/useHolidayList';
import { useHolidayByRegionList } from '@/hooks/attendance/holiday/useHolidayByRegionList';
import { useHolidayTypeOptions } from '@/hooks/attendance/holiday/useHolidayTypeOptions';
import HolidayListTab from '@/components/features/attendance/holiday/HolidayListTab';
import HolidayByRegionTab from '@/components/features/attendance/holiday/HolidayByRegionTab';


type HolidayTab = 'holidays' | 'holidaysByRegion';

export const HolidayListPage: React.FC = () => {
  const { t: translate } = useTranslation();
  const [activeTab, setActiveTab] = useState<HolidayTab>('holidays');

  const { holidayTypeOptions } = useHolidayTypeOptions();

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    openCreatePanel,
    onExport,
    isExporting,
    filters,
    setFilters,
    yearOptions,
    monthOptions,
  } = useHolidayList();

  const {
    rows: regionRows,
    total: regionTotal,
    page: regionPage,
    pageSize: regionPageSize,
    setPage: regionSetPage,
    setPageSize: regionSetPageSize,
    columns: regionColumns,
    actions: regionActions,
    onSearch: regionOnSearch,
    onSortChange: regionOnSortChange,
    loading: regionLoading,
    openCreatePanel: regionOpenCreatePanel,
    onExport: regionOnExport,
    isExporting: regionIsExporting,
    filters: regionFilters,
    setFilters: regionSetFilters,
    yearOptions: regionYearOptions,
    monthOptions: regionMonthOptions,
    regionOptions,
  } = useHolidayByRegionList();

  const tabs = useMemo(
    () => [
      {
        id: 'holidays',
        label: translate('attendance:holiday.tabs.holidays'),
        content: (
          <HolidayListTab
            rows={rows}
            total={total}
            page={page}
            pageSize={pageSize}
            setPage={setPage}
            setPageSize={setPageSize}
            columns={columns}
            actions={actions}
            onSearch={onSearch}
            onSortChange={onSortChange}
            loading={loading}
            openCreatePanel={openCreatePanel}
            onExport={onExport}
            isExporting={isExporting}
            filters={filters}
            setFilters={setFilters}
            yearOptions={yearOptions}
            monthOptions={monthOptions}
            holidayTypeOptions={holidayTypeOptions}
          />
        ),
      },
      {
        id: 'holidaysByRegion',
        label: translate('attendance:holiday.tabs.holidaysByRegion'),
        content: (
          <HolidayByRegionTab
            rows={regionRows}
            total={regionTotal}
            page={regionPage}
            pageSize={regionPageSize}
            setPage={regionSetPage}
            setPageSize={regionSetPageSize}
            columns={regionColumns}
            actions={regionActions}
            onSearch={regionOnSearch}
            onSortChange={regionOnSortChange}
            loading={regionLoading}
            openCreatePanel={regionOpenCreatePanel}
            onExport={regionOnExport}
            isExporting={regionIsExporting}
            filters={regionFilters}
            setFilters={regionSetFilters}
            yearOptions={regionYearOptions}
            monthOptions={regionMonthOptions}
            regionOptions={regionOptions}
            holidayTypeOptions={holidayTypeOptions}
          />
        ),
      },
    ],
    [
      translate,
      rows,
      total,
      page,
      pageSize,
      setPage,
      setPageSize,
      columns,
      actions,
      onSearch,
      onSortChange,
      loading,
      openCreatePanel,
      onExport,
      isExporting,
      filters,
      setFilters,
      yearOptions,
      monthOptions,
      holidayTypeOptions,
      regionRows,
      regionTotal,
      regionPage,
      regionPageSize,
      regionSetPage,
      regionSetPageSize,
      regionColumns,
      regionActions,
      regionOnSearch,
      regionOnSortChange,
      regionLoading,
      regionOpenCreatePanel,
      regionOnExport,
      regionIsExporting,
      regionFilters,
      regionSetFilters,
      regionYearOptions,
      regionMonthOptions,
      regionOptions,
    ],
  );

  return (
    <PageLayout>
      <div className="flex flex-col gap-6" data-testid="holiday-list-page">
        <TabList
          tabs={tabs}
          defaultActiveTab={activeTab}
          onTabChange={(id) => setActiveTab(id as HolidayTab)}
        />
      </div>
    </PageLayout>
  );
};

export default HolidayListPage;
